import { CompassOutlined } from "@ant-design/icons";
import { Flex, Image as ImageComponent, notification } from "antd";
import { FC, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Coords, Market, TaskSetGeoLocation } from "tm-shared/types";

import BaseButton from "@/components/BaseButton";
import Header from "@/components/Header";
import { MainContainer } from "@/components/Layout/Styled";
import { Loading } from "@/components/Loading";
import { SVScanButton } from "@/components/SVScanButton";
import { PrimaryText } from "@/components/Typography/Styled";
import { FALLBACK_IMAGE, ROUTES } from "@/constants";
import { useAppDispatch } from "@/hooks/redux";
import { makeCommand, useMakeCommand } from "@/hooks/useRequestQueue";
import { enqueueRequestQueueItem } from "@/store/slices/offline";
import { setTaskCompleted } from "@/store/slices/work";
import { AnyQueueCommand } from "@/types/requestQueue";
import { getFileUrl, getPreciseLocation } from "@/utils";

import { StyledImageWrapper } from "./Styled";

interface SendGeoPageProps {
    task: TaskSetGeoLocation;
    market: Market;
}

export const SendGeoPage: FC<SendGeoPageProps> = ({ task, market }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { makeTaskCompleteCommands } = useMakeCommand();

    const [loading, setLoading] = useState(false);

    const taskId = task.id;
    const marketId = market.id;

    const handleSendGeo = useCallback(async () => {
        let coords: Coords | null = null;

        try {
            setLoading(true);

            coords = await getPreciseLocation();
        } catch (error) {
            console.error(error);

            const geoErr = error as GeolocationPositionError;
            if (geoErr && typeof geoErr === "object" && "code" in geoErr) {
                if (geoErr.code === 1) {
                    notification.error({
                        message:
                            "Не вдалося отримати вашу геолокацію. Дозвольте доступ та спробуйте ще раз.",
                    });

                    return;
                }
            }
        } finally {
            setLoading(false);
        }

        const commands: AnyQueueCommand[] = [];

        if (coords) {
            commands.push(
                makeCommand({
                    type: "postMarketChangeGeo",
                    title: "Збереження координат торгової точки",
                    params: { marketId, geo: `${coords.lat},${coords.lng}` },
                }),
            );
        }

        const taskCompleteCommands = await makeTaskCompleteCommands({
            params: {
                taskId,
                hasPhoto: false,
                payload: { result: !!coords, geo: coords },
            },
            marketId,
            coords,
        });

        commands.push(...taskCompleteCommands);

        dispatch(
            enqueueRequestQueueItem({
                id: taskId,
                metadata: { marketId, taskId },
                commands,
            }),
        );

        dispatch(setTaskCompleted({ marketId, taskId }));

        navigate(ROUTES.WorkPlan, { replace: true });
    }, [makeTaskCompleteCommands, taskId, marketId, dispatch, navigate]);

    return (
        <>
            {loading && <Loading title="Завершення задачі" />}

            <Header
                title={task.name}
                back
                rightActions={
                    <SVScanButton marketId={marketId} taskId={taskId} />
                }
            />

            <MainContainer>
                <Flex vertical gap={16}>
                    <PrimaryText fontSize={16}>
                        ТОВ &quot;{market.name}&quot;
                    </PrimaryText>

                    <StyledImageWrapper>
                        <ImageComponent
                            width="100%"
                            height="100%"
                            src={getFileUrl(market.photo)}
                            fallback={FALLBACK_IMAGE}
                            alt="Market photo"
                        />
                    </StyledImageWrapper>

                    <BaseButton
                        type="primary"
                        icon={<CompassOutlined />}
                        block
                        onClick={handleSendGeo}
                        loading={loading}
                    >
                        Поділитися геолокацією
                    </BaseButton>
                </Flex>
            </MainContainer>
        </>
    );
};
